import { FC } from 'react'
import { User, Building, MapPin, Globe, Briefcase } from 'lucide-react'
import { isFieldWhitelisted, getCanonicalFieldName } from 'src/utils'
import { DBT } from 'types/internal'
import { forEach } from 'lodash'

interface Props {
  user?: DBT.VegasUserT
}

// Whitelist of fields to display (refined for clarity)
const FIELD_WHITELIST = [
  'name',
  // 'firstName',
  // 'lastName',
  'email',
  'phone',
  'company',
  'jobTitle',
  'title',
  'department',
  'location',
  'city',
  'state',
  'country',
  'website',
  'linkedinUrl',
  'personLinkedinUrl',
  'companyLinkedinUrl',
  'industry',
  'companyFoundedYear'
]

// Group fields for organized display (mutually exclusive)
const FIELD_GROUPS = {
  personal: ['name', 'email', 'phone'],
  professional: [
    'company',
    'jobTitle',
    'title',
    'department',
    'industry',
    'companyFoundedYear'
  ],
  location: ['location', 'city', 'state', 'country'],
  links: ['website', 'linkedinUrl', 'personLinkedinUrl', 'companyLinkedinUrl']
}

// Format field name for display
const formatFieldName = (fieldName: string): string => {
  // Special cases
  const specialCases: Record<string, string> = {
    jobTitle: 'Job Title',
    linkedinUrl: 'LinkedIn',
    personLinkedinUrl: 'LinkedIn',
    companyLinkedinUrl: 'Company LinkedIn',
    companyFoundedYear: 'Founded'
  }

  if (specialCases[fieldName]) return specialCases[fieldName]

  // Convert camelCase to Title Case
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
}

// Format field value for display
const formatFieldValue = (
  value: any,
  fieldName: string
): string | JSX.Element => {
  if (value === null || value === undefined || value === '') return '-'

  // Special formatting for LinkedIn URLs
  if (
    typeof value === 'string' &&
    fieldName.toLowerCase().includes('linkedin')
  ) {
    // Extract username from LinkedIn URL if possible
    const match = value.match(/linkedin\.com\/(?:in|company)\/([^/]+)/)
    const displayText = match ? match[1] : 'View Profile'
    return (
      <a
        href={value.startsWith('http') ? value : `https://${value}`}
        target='_blank'
        rel='noopener noreferrer'
        className='text-blue-600 hover:text-blue-700'
      >
        {displayText}
      </a>
    )
  }

  // Special formatting for other URLs
  if (
    typeof value === 'string' &&
    (fieldName.toLowerCase().includes('url') ||
      fieldName.toLowerCase().includes('website'))
  ) {
    const displayUrl = value
      .replace(/^https?:\/\/(www\.)?/, '')
      .replace(/\/$/, '')
    return (
      <a
        href={value.startsWith('http') ? value : `https://${value}`}
        target='_blank'
        rel='noopener noreferrer'
        className='text-blue-600 hover:text-blue-700'
      >
        {displayUrl}
      </a>
    )
  }

  // Special formatting for email
  if (fieldName.toLowerCase().includes('email')) {
    return (
      <a href={`mailto:${value}`} className='text-blue-600 hover:text-blue-700'>
        {value}
      </a>
    )
  }

  return String(value)
}

const RecognitionSection: FC<Props> = ({ user }) => {
  const recognitionData = user

  // Filter and organize fields
  const displayFields: Record<string, any> = {}
  const processedFields = new Set<string>()

  // Process all fields through whitelist and fuzzy matching
  forEach(recognitionData, (value, key) => {
    // Skip if already processed or if it's an array/object (like photos)
    if (processedFields.has(key) || typeof value === 'object') return

    if (isFieldWhitelisted(key, FIELD_WHITELIST)) {
      const canonicalName = getCanonicalFieldName(key, FIELD_WHITELIST)
      displayFields[canonicalName] = value
      processedFields.add(key)
    }
  })

  // If no fields to display, don't render the section
  if (Object.keys(displayFields).length === 0) return null

  // Group fields for display - each field appears in only one group
  const groupedFields: Array<{
    title: string
    icon: JSX.Element
    fields: Array<[string, any]>
  }> = []
  const usedFields = new Set<string>()

  // Helper to add fields to a group without duplicates
  const getGroupFields = (groupKeys: string[]) => {
    return Object.entries(displayFields).filter(([key]) => {
      if (usedFields.has(key)) return false
      const matches = groupKeys.some(
        (field) => key.toLowerCase() === field.toLowerCase()
      )
      if (matches) usedFields.add(key)
      return matches
    })
  }

  // Personal Info
  const personalFields = getGroupFields(FIELD_GROUPS.personal)
  if (personalFields.length > 0) {
    groupedFields.push({
      title: 'Personal Information',
      icon: <User size={16} className='text-blue-500' />,
      fields: personalFields
    })
  }

  // Professional Info
  const professionalFields = getGroupFields(FIELD_GROUPS.professional)
  if (professionalFields.length > 0) {
    groupedFields.push({
      title: 'Professional Information',
      icon: <Briefcase size={16} className='text-blue-500' />,
      fields: professionalFields
    })
  }

  // Location Info
  const locationFields = getGroupFields(FIELD_GROUPS.location)
  if (locationFields.length > 0) {
    groupedFields.push({
      title: 'Location',
      icon: <MapPin size={16} className='text-blue-500' />,
      fields: locationFields
    })
  }

  // Links
  const linkFields = getGroupFields(FIELD_GROUPS.links)
  if (linkFields.length > 0) {
    groupedFields.push({
      title: 'Links',
      icon: <Globe size={16} className='text-blue-500' />,
      fields: linkFields
    })
  }

  // Additional Info (fields not in other groups)
  const additionalFields = Object.entries(displayFields).filter(
    ([key]) => !usedFields.has(key)
  )
  if (additionalFields.length > 0) {
    groupedFields.push({
      title: 'Additional Information',
      icon: <Building size={16} className='text-blue-500' />,
      fields: additionalFields
    })
  }

  return (
    <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
      <div className='mb-4'>
        <h2 className='text-lg font-semibold text-gray-900'>Background Data</h2>
      </div>

      <div className='space-y-4'>
        {groupedFields.map((group, groupIdx) => (
          <div
            key={groupIdx}
            className={groupIdx > 0 ? 'pt-3 border-t border-gray-100' : ''}
          >
            <div className='flex items-center gap-2 mb-3'>
              {group.icon}
              <h3 className='text-sm font-semibold text-gray-700'>
                {group.title}
              </h3>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3 pl-6'>
              {group.fields.map(([fieldName, value]) => (
                <div key={fieldName} className='min-w-0'>
                  <div className='text-xs text-gray-400 mb-0.5'>
                    {formatFieldName(fieldName)}
                  </div>
                  <div className='text-sm text-gray-900'>
                    {formatFieldValue(value, fieldName)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default RecognitionSection
